import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format, addDays } from 'date-fns';
import { el } from 'date-fns/locale';
import { serviceApi, staffApi, clientApi } from '../services/api';
import './BookingModal.css';

const DEFAULT_SERVICES = [
  { id: null, name: 'Κόψιμο', duration: 30 },
  { id: null, name: 'Βαφή', duration: 90 },
  { id: null, name: 'Χτένισμα', duration: 30 }
];

const formatTime12 = (h, m) => {
  const suffix = h < 12 ? 'ΠΜ' : 'ΜΜ';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${suffix}`;
};

const TIME_OPTIONS = (() => {
  const opts = [];
  for (let h = 0; h < 24; h++)
    for (let m = 0; m < 60; m += 5)
      opts.push({ val: `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`, label: formatTime12(h, m) });
  return opts;
})();

const DATE_OPTIONS = (() => {
  const opts = [], today = new Date(); today.setHours(0,0,0,0);
  for (let i = 0; i < 60; i++) {
    const d = addDays(today, i);
    opts.push({ val: format(d, 'yyyy-MM-dd'), label: format(d, 'EEEE, d MMMM yyyy', { locale: el }) });
  }
  return opts;
})();

export default function BookingModal({ initialDate, existingBooking, schedule, onSave, onDelete, onClose }) {
  const [services, setServices]   = useState(DEFAULT_SERVICES);
  const [staffList, setStaffList] = useState([]);
  const [saving, setSaving]       = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // ── Autocomplete: δύο ανεξάρτητα dropdowns ────────────────
  const [nameSuggestions, setNameSuggestions]       = useState([]);
  const [showNameSugg, setShowNameSugg]             = useState(false);
  const [searchingName, setSearchingName]           = useState(false);

  const [phoneSuggestions, setPhoneSuggestions]     = useState([]);
  const [showPhoneSugg, setShowPhoneSugg]           = useState(false);
  const [searchingPhone, setSearchingPhone]         = useState(false);

  const [selectedClient, setSelectedClient]         = useState(null);

  const nameTimeout  = useRef(null);
  const phoneTimeout = useRef(null);
  const nameWrapRef  = useRef(null);
  const phoneWrapRef = useRef(null);
  const nameInputRef = useRef(null);
  const phoneInputRef = useRef(null);
  const [nameDropPos, setNameDropPos]   = useState(null);
  const [phoneDropPos, setPhoneDropPos] = useState(null);

  const isEdit = !!existingBooking;

  const initDate = existingBooking
    ? format(new Date(existingBooking.starts_at), 'yyyy-MM-dd')
    : initialDate ? format(initialDate, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  const initTime = existingBooking
    ? `${String(new Date(existingBooking.starts_at).getHours()).padStart(2,'0')}:${String(new Date(existingBooking.starts_at).getMinutes()).padStart(2,'0')}`
    : initialDate
      ? `${String(initialDate.getHours()).padStart(2,'0')}:${String(initialDate.getMinutes()).padStart(2,'0')}`
      : '09:00';

  const [form, setForm] = useState({
    client_name:      existingBooking?.client_name  || '',
    client_phone:     existingBooking?.client_phone || '',
    service_id:       existingBooking?.service_id   || null,
    service_name:     existingBooking?.service_name || DEFAULT_SERVICES[0].name,
    service_duration: existingBooking?.service_duration || DEFAULT_SERVICES[0].duration,
    service_price:    existingBooking?.service?.price || '',
    staff_id:         existingBooking?.staff_id     || '',
    date:             initDate,
    time:             initTime,
    notes:            existingBooking?.notes        || '',
    tip:              existingBooking?.tip      != null ? String(existingBooking.tip)      : '',
    discount:         existingBooking?.discount != null ? String(existingBooking.discount) : '',
  });

  // Load services & staff
  useEffect(() => {
    serviceApi.getAll().then(r => {
      const svcs = r.data?.services || r.data;
      if (svcs?.length) {
        setServices(svcs);
        // ── Auto-select πρώτη υπηρεσία μόλις φορτωθούν ──────────
        // Μόνο για νέα κράτηση (όχι edit) και μόνο αν δεν έχει
        // ήδη service_id (δηλαδή ήταν στα DEFAULT_SERVICES)
        if (!existingBooking) {
          setForm(f => ({
            ...f,
            service_name:     svcs[0].name,
            service_duration: svcs[0].duration,
            service_price:    svcs[0].price || '',
            service_id:       svcs[0].id   || null,
            discount:         '',  // reset discount όταν αλλάζει υπηρεσία
          }));
        } else if (existingBooking.service_id) {
          // Edit: βρες την υπηρεσία για να πάρεις την τιμή
          const match = svcs.find(s => s.id === existingBooking.service_id);
          if (match) {
            setForm(f => ({
              ...f,
              service_price: match.price || '',
            }));
          }
        }
      }
    }).catch(() => {});
    staffApi.getAll().then(r => {
      const st = r.data?.staff || r.data;
      if (st?.length) setStaffList(st);
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Κλείσιμο dropdowns όταν κλικάρεις έξω
  useEffect(() => {
    const handler = (e) => {
      if (nameWrapRef.current && !nameWrapRef.current.contains(e.target))
        setShowNameSugg(false);
      if (phoneWrapRef.current && !phoneWrapRef.current.contains(e.target))
        setShowPhoneSugg(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleServiceChange = (e) => {
    const svc = services.find(s => s.name === e.target.value);
    if (svc) setForm(f => ({
      ...f,
      service_name:     svc.name,
      service_duration: svc.duration,
      service_price:    svc.price || '',
      service_id:       svc.id   || null,
      discount:         '',  // reset έκπτωση όταν αλλάζει υπηρεσία
    }));
  };

  // ── Search by name ─────────────────────────────────────────
  const searchByName = useCallback((q) => {
    if (!q || q.length < 2) { setNameSuggestions([]); setShowNameSugg(false); return; }
    setSearchingName(true);
    clientApi.search(q)
      .then(r => {
        const clients = (r.data?.clients || []).filter(c =>
          c.name?.toLowerCase().includes(q.toLowerCase())
        );
        setNameSuggestions(clients);
        setShowNameSugg(clients.length > 0);
      })
      .catch(() => setNameSuggestions([]))
      .finally(() => setSearchingName(false));
  }, []);

  // ── Search by phone ────────────────────────────────────────
  const searchByPhone = useCallback((q) => {
    if (!q || q.length < 3) { setPhoneSuggestions([]); setShowPhoneSugg(false); return; }
    setSearchingPhone(true);
    clientApi.search(q)
      .then(r => {
        const clients = (r.data?.clients || []).filter(c =>
          c.phone?.includes(q)
        );
        setPhoneSuggestions(clients);
        setShowPhoneSugg(clients.length > 0);
      })
      .catch(() => setPhoneSuggestions([]))
      .finally(() => setSearchingPhone(false));
  }, []);

  const handleNameChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, client_name: val }));
    setSelectedClient(null);
    setShowPhoneSugg(false);
    setNameDropPos(calcPos(nameInputRef));
    clearTimeout(nameTimeout.current);
    nameTimeout.current = setTimeout(() => searchByName(val), 300);
  };

  const handlePhoneChange = (e) => {
    const val = e.target.value;
    setForm(f => ({ ...f, client_phone: val }));
    setSelectedClient(null);
    setShowNameSugg(false);
    setPhoneDropPos(calcPos(phoneInputRef));
    clearTimeout(phoneTimeout.current);
    phoneTimeout.current = setTimeout(() => searchByPhone(val), 300);
  };

  const selectClient = (client) => {
    setSelectedClient(client);
    setForm(f => ({
      ...f,
      client_name:  client.name,
      client_phone: client.phone || '',
    }));
    setNameSuggestions([]);
    setPhoneSuggestions([]);
    setShowNameSugg(false);
    setShowPhoneSugg(false);
  };

  // ── Dropdown position (fixed, escapes modal overflow) ────────
  const calcPos = (ref) => {
    if (!ref.current) return null;
    const r = ref.current.getBoundingClientRect();
    return { top: r.bottom + 4, left: r.left, width: r.width };
  };

  // ── Validation ─────────────────────────────────────────────
  const isNewClient    = !selectedClient && !isEdit;
  const phoneRequired  = isNewClient;

  // ── Υπολογισμός effective τιμής (discount παρακάμπτει service_price) ──
  const effectivePrice = form.discount && parseFloat(form.discount) > 0
    ? parseFloat(form.discount)
    : form.service_price ? parseFloat(form.service_price) : null;

  const hasDiscount = form.discount && parseFloat(form.discount) > 0
    && form.service_price && parseFloat(form.service_price) > 0
    && parseFloat(form.discount) !== parseFloat(form.service_price);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.date || !form.time) return;
    if (phoneRequired && !form.client_phone.trim()) {
      alert('Παρακαλώ συμπληρώστε τηλέφωνο για τον νέο πελάτη.');
      return;
    }
    setSaving(true);
    const [h, m] = form.time.split(':').map(Number);
    const d = new Date(form.date);
    d.setHours(h, m, 0, 0);
    try {
      await onSave({
        ...(isEdit ? { id: existingBooking.id } : {}),
        client_name:      form.client_name,
        client_phone:     form.client_phone,
        service_id:       form.service_id   || null,
        service_name:     form.service_name,
        service_duration: form.service_duration,
        staff_id:         form.staff_id     || null,
        starts_at:        d.toISOString(),
        notes:            form.notes,
        tip:              form.tip      ? parseFloat(form.tip)      : null,
        discount:         form.discount ? parseFloat(form.discount) : null,
        source:           'manual'
      });
    } finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!confirmDelete) { setConfirmDelete(true); return; }
    await onDelete(existingBooking.id);
  };

  const startLabel = formatTime12(...form.time.split(':').map(Number));
  const endLabel = (() => {
    const [h, m] = form.time.split(':').map(Number);
    const d = new Date(form.date); d.setHours(h, m, 0, 0);
    const end = new Date(d.getTime() + form.service_duration * 60000);
    return formatTime12(end.getHours(), end.getMinutes());
  })();
  const dateLabel = (() => { try { return format(new Date(form.date), 'EEEE, d MMMM', { locale: el }); } catch { return ''; } })();

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <div className="modal-header-info">
            <span className="modal-title">{isEdit ? '✏️ Επεξεργασία κράτησης' : 'Νέα κράτηση'}</span>
            <div className="modal-time-range">
              <span className="modal-time-badge">{startLabel} – {endLabel}</span>
              <span className="modal-date-text">{dateLabel}</span>

              {/* ── Price badge: δείχνει έκπτωση αν υπάρχει ── */}
              {effectivePrice != null && (
                <span className="modal-price-badge">
                  💶{' '}
                  {hasDiscount ? (
                    <>
                      <span style={{ textDecoration: 'line-through', opacity: 0.5, marginRight: 4 }}>
                        {parseFloat(form.service_price).toFixed(2)}€
                      </span>
                      {effectivePrice.toFixed(2)}€
                    </>
                  ) : (
                    `${effectivePrice.toFixed(2)} €`
                  )}
                  {form.tip && parseFloat(form.tip) > 0 && (
                    <span style={{ marginLeft: 5, color: '#16a34a' }}>
                      +€{parseFloat(form.tip).toFixed(2)} tip
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'contents' }}>
          <div className="modal-body">

            {/* ── Όνομα με autocomplete by name ── */}
            <div className="field" ref={nameWrapRef} style={{ position: 'relative' }}>
              <label>
                Όνομα πελάτη *
                {selectedClient && (
                  <span style={{ marginLeft: 8, color: 'var(--accent)', fontWeight: 700, fontSize: 10 }}>
                    ✓ Υπάρχων πελάτης
                  </span>
                )}
              </label>
              <input
                ref={nameInputRef}
                type="text"
                placeholder="π.χ. Μαρία Παπαδοπούλου"
                value={form.client_name}
                onChange={handleNameChange}
                onFocus={() => { setNameDropPos(calcPos(nameInputRef)); nameSuggestions.length > 0 && setShowNameSugg(true); }}
                autoComplete="off"
                required
              />
              {searchingName && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>Αναζήτηση...</div>
              )}
              {showNameSugg && nameSuggestions.length > 0 && nameDropPos && (
                <div className="autocomplete-dropdown autocomplete-fixed"
                  style={{ top: nameDropPos.top, left: nameDropPos.left, width: nameDropPos.width }}>
                  {nameSuggestions.map(c => (
                    <div key={c.id} className="autocomplete-item" onMouseDown={() => selectClient(c)}>
                      <span className="autocomplete-name">{c.name}</span>
                      {c.phone && <span className="autocomplete-phone">{c.phone}</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Τηλέφωνο με autocomplete by phone ── */}
            <div className="field" ref={phoneWrapRef} style={{ position: 'relative' }}>
              <label>
                Τηλέφωνο {phoneRequired ? '*' : ''}
              </label>
              <input
                ref={phoneInputRef}
                type="tel"
                placeholder="69ΧΧΧΧΧΧΧΧ"
                value={form.client_phone}
                onChange={handlePhoneChange}
                onFocus={() => { setPhoneDropPos(calcPos(phoneInputRef)); phoneSuggestions.length > 0 && setShowPhoneSugg(true); }}
                required={phoneRequired}
                autoComplete="off"
                style={selectedClient ? { background: 'var(--accent-light)', borderColor: 'var(--accent)' } : {}}
              />
              {searchingPhone && (
                <div style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 3 }}>Αναζήτηση...</div>
              )}
              {showPhoneSugg && phoneSuggestions.length > 0 && phoneDropPos && (
                <div className="autocomplete-dropdown autocomplete-fixed"
                  style={{ top: phoneDropPos.top, left: phoneDropPos.left, width: phoneDropPos.width }}>
                  {phoneSuggestions.map(c => (
                    <div key={c.id} className="autocomplete-item" onMouseDown={() => selectClient(c)}>
                      <span className="autocomplete-phone" style={{ fontWeight: 700 }}>{c.phone}</span>
                      <span className="autocomplete-name">{c.name}</span>
                    </div>
                  ))}
                </div>
              )}
              {phoneRequired && !form.client_phone && (
                <div className="field-hint" style={{ color: 'var(--text-muted)' }}>
                  Απαραίτητο για την καταχώρηση νέου πελάτη
                </div>
              )}
            </div>

            {staffList.length > 0 && (
              <div className="field">
                <label>Υπάλληλος</label>
                <div className="staff-picker">
                  <button
                    type="button"
                    className={"staff-pick-btn" + (form.staff_id === '' ? ' active-none' : '')}
                    onClick={() => setForm(f => ({ ...f, staff_id: '' }))}
                  >
                    <span className="staff-pick-dot" style={{ background: '#ccc' }} />
                    <span>Χωρίς</span>
                  </button>
                  {staffList.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      className={"staff-pick-btn" + (form.staff_id === s.id ? ' active' : '')}
                      style={{ '--scolor': s.color }}
                      onClick={() => setForm(f => ({ ...f, staff_id: s.id }))}
                    >
                      <span className="staff-pick-dot" style={{ background: s.color }} />
                      <span>{s.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="field">
              <label>Υπηρεσία</label>
              <select value={form.service_name} onChange={handleServiceChange}>
                {services.map(s => (
                  <option key={s.name} value={s.name}>
                    {s.name} — {s.duration} λεπτά{s.price ? ` · ${parseFloat(s.price).toFixed(2)}€` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Ημερομηνία</label>
              <select value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}>
                {DATE_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Ώρα</label>
              <select value={form.time} onChange={e => setForm(f => ({ ...f, time: e.target.value }))}>
                {TIME_OPTIONS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
              </select>
            </div>

            <div className="field">
              <label>Διάρκεια (λεπτά)</label>
              <input type="number" min="5" max="480" step="5" value={form.service_duration}
                onChange={e => setForm(f => ({ ...f, service_duration: parseInt(e.target.value) || 30 }))} />
              <div className="field-hint">Αλλάζει αυτόματα με την υπηρεσία</div>
            </div>

            <div className="field">
              <label>Σημειώσεις</label>
              <input type="text" placeholder="Προαιρετικές σημειώσεις..." value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            {/* ── Έκπτωση / Τιμή παράκαμψης ── */}
            <div className="field">
              <label>
                🏷️ Τιμή / Έκπτωση
                {form.service_price && !form.discount && (
                  <span style={{ marginLeft: 6, color: 'var(--text-muted)', fontWeight: 400, fontSize: 10 }}>
                    (κανονική τιμή: {parseFloat(form.service_price).toFixed(2)}€)
                  </span>
                )}
                {hasDiscount && (
                  <span style={{ marginLeft: 8, color: '#dc2626', fontWeight: 700, fontSize: 11 }}>
                    −{(parseFloat(form.service_price) - parseFloat(form.discount)).toFixed(2)}€ έκπτωση
                  </span>
                )}
              </label>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder={
                    form.service_price
                      ? `Κανονική: ${parseFloat(form.service_price).toFixed(2)}€ — βάλε νέα τιμή`
                      : 'Τιμή €'
                  }
                  value={form.discount}
                  onChange={e => setForm(f => ({ ...f, discount: e.target.value }))}
                  style={{ flex: 1 }}
                />
                {form.discount && (
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, discount: '' }))}
                    style={{
                      background: 'none',
                      border: '1.5px solid var(--border-medium)',
                      borderRadius: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontSize: 12,
                      color: 'var(--text-muted)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    ✕ Reset
                  </button>
                )}
              </div>
              <div className="field-hint">
                Αφήστε κενό για να χρησιμοποιηθεί η τιμή της υπηρεσίας
              </div>
            </div>

            {/* ── Φιλοδώρημα ── */}
            <div className="field tip-field">
              <label>
                💰 Φιλοδώρημα
                {form.tip && parseFloat(form.tip) > 0 && (
                  <span style={{ marginLeft: 8, color: 'var(--green, #16a34a)', fontWeight: 700, fontSize: 11 }}>
                    +€{parseFloat(form.tip).toFixed(2)}
                  </span>
                )}
              </label>
              <div className="tip-input-row">
                <div className="tip-presets">
                  {['0','1','2','5','10'].map(v => (
                    <button
                      key={v}
                      type="button"
                      className={"tip-preset" + (form.tip === v ? ' active' : '')}
                      onClick={() => setForm(f => ({ ...f, tip: v }))}
                    >
                      {v === '0' ? 'Κανένα' : `€${v}`}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="Άλλο €"
                  value={form.tip}
                  onChange={e => setForm(f => ({ ...f, tip: e.target.value }))}
                  style={{ width: 90 }}
                />
              </div>
            </div>

          </div>

          <div className="modal-footer">
            {isEdit && (
              <button type="button" className="btn btn-danger" onClick={handleDelete} style={{ marginRight: 'auto' }}>
                {confirmDelete ? '⚠️ Επιβεβαίωση;' : '🗑️ Διαγραφή'}
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={onClose}>Ακύρωση</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Αποθήκευση...' : isEdit ? '💾 Αποθήκευση αλλαγών' : 'Αποθήκευση'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}